import {
	Box,
	CircularProgress,
	IconButton,
	Slider,
	Typography,
} from '@mui/material';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { ArrowBack } from 'components/icons';
import Webcam from 'react-webcam';
import Lottie from 'lottie-react';
import tapAnimation from '../home/tapAnimation.json';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import {
	extractVin,
	VinVehicleInfo,
	VinExtractionError,
} from 'api/canpark/scanning/extractVin';
import { useNotificationProvider } from 'hooks';
import { normalizeVin } from '../../utils/normalizeVin';
import { cropImage } from '../../utils/cropImage';
import { debounce } from 'lodash';

interface VinScanningComponentProps {
	onVinScanned: (vin: string, vehicleInfo: VinVehicleInfo | null) => void;
	isCameraOn: boolean;
	setIsCameraOn: (val: boolean) => void;
}

const VinScanningComponent: React.FC<VinScanningComponentProps> = ({
	onVinScanned,
	isCameraOn,
	setIsCameraOn,
}) => {
	const [capturedImage, setCapturedImage] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const webcamRef = useRef<Webcam | null>(null);
	const [showScanAnimation, setShowScanAnimation] = useState(false);
	const [isWebcamLoaded, setIsWebcamLoaded] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(1);
	const [minZoom, setMinZoom] = useState(1);
	const [maxZoom, setMaxZoom] = useState(3);
	const viewFinderBoxRef = useRef<HTMLDivElement | null>(null);
	const { notifyError, notifySuccess } = useNotificationProvider();

	// Debounced applyZoom function
	const debouncedApplyZoom = useCallback(
		debounce((zoomValue: number) => {
			if (webcamRef.current && webcamRef.current.video) {
				const videoTrack =
					webcamRef.current.stream?.getVideoTracks()[0];
				if (videoTrack) {
					const capabilities = videoTrack.getCapabilities();
					if ('zoom' in capabilities) {
						(videoTrack as any).applyConstraints({
							advanced: [{ zoom: zoomValue }],
						});
					}
				}
			}
		}, 100),
		[],
	);

	// Initialize zoom capabilities when webcam loads
	useEffect(() => {
		if (webcamRef.current && webcamRef.current.video) {
			const videoTrack = webcamRef.current.stream?.getVideoTracks()[0];
			if (videoTrack) {
				const capabilities = videoTrack.getCapabilities();
				if ('zoom' in capabilities) {
					const zoomCapabilities = capabilities.zoom as {
						min: number;
						max: number;
					};
					const zoomMin = 1;
					const zoomMax = Math.min(zoomCapabilities.max, 10);
					setMinZoom(zoomMin);
					setMaxZoom(zoomMax);
				} else {
					setMinZoom(1);
					setMaxZoom(1);
				}
			}
		}
	}, [isWebcamLoaded]);

	// Show tap animation when webcam loads
	useEffect(() => {
		if (isWebcamLoaded) {
			setShowScanAnimation(true);
			const timer = setTimeout(() => setShowScanAnimation(false), 4000);
			return () => clearTimeout(timer);
		}
	}, [isWebcamLoaded]);

	// Reset state when camera is turned off
	useEffect(() => {
		if (!isCameraOn) {
			setCapturedImage(null);
			setIsLoading(false);
			setIsWebcamLoaded(false);
			setZoomLevel(1);
		}
	}, [isCameraOn]);

	const capture = async () => {
		if (!webcamRef.current || isLoading) return;

		const imageSrc = webcamRef.current.getScreenshot();
		if (!imageSrc) return;

		const viewFinderWidth = viewFinderBoxRef?.current?.clientWidth;
		const viewFinderHeight = viewFinderBoxRef?.current?.clientHeight;

		const croppedImage = await cropImage(
			imageSrc,
			viewFinderWidth,
			viewFinderHeight,
		);
		setCapturedImage(croppedImage);
		setIsLoading(true);

		try {
			const data = await extractVin(croppedImage);
			const normalizedVin = normalizeVin(data.vin);

			if (!normalizedVin) {
				notifyError('Could not extract a valid VIN from the image');
				return;
			}

			onVinScanned(normalizedVin, data.vehicleInfo);

			if (data.vehicleInfo) {
				notifySuccess('VIN scanned and vehicle info decoded successfully');
			} else {
				notifySuccess(
					'VIN scanned successfully. Vehicle info could not be decoded - please enter manually.',
				);
			}
			setIsCameraOn(false);
			} catch (error) {
			if (error instanceof VinExtractionError) {
				notifyError(error.message);
			} else {
				notifyError('Failed to extract VIN. Please try again.');
			}
			console.error('[VinScanningComponent] Failed to extract VIN:', error);
			// Reset captured image on error so user can try again
			setCapturedImage(null);
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setIsCameraOn(false);
	};

	if (!isCameraOn) {
		return null;
	}

	return (
		<Box display="flex" flexDirection="column" alignItems="center">
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					position: 'relative',
					width: '100%',
				}}
			>
				{isLoading && capturedImage ? (
					<Box sx={{ position: 'relative', width: '100%' }}>
						<img
							src={capturedImage}
							alt="Captured VIN"
							style={{
								width: '100%',
								height: 'auto',
								borderRadius: '8px',
							}}
						/>
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
								backgroundColor: 'rgba(0, 0, 0, 0.5)',
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center',
								borderRadius: '8px',
								gap: 2,
							}}
						>
							<CircularProgress color="inherit" />
							<Typography variant="body2" color="white">
								Extracting VIN...
							</Typography>
						</Box>
					</Box>
				) : (
					<Box
						display="flex"
						flexDirection="column"
						width="100%"
					>
						<Box
							display="flex"
							justifyContent="space-between"
							width="100%"
							mb={1}
						>
							<IconButton
								onClick={handleClose}
								color="error"
								disabled={isLoading}
								sx={{
									border: '1px solid',
									borderColor: 'error.main',
								}}
							>
								<ArrowBack />
							</IconButton>
						</Box>

						<Box
							ref={viewFinderBoxRef}
							sx={{
								position: 'relative',
								width: '100%',
								maxWidth: '500px',
								aspectRatio: '2.35 / 1',
							}}
						>
							{/* Loading overlay while webcam initializes */}
							{!isWebcamLoaded && (
								<Box
									sx={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: '100%',
										height: '100%',
										bgcolor: 'rgba(0,0,0,0.3)',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										borderRadius: '8px',
										zIndex: 10,
									}}
								>
									<CircularProgress color="inherit" />
								</Box>
							)}

							{/* Tap animation overlay */}
							{showScanAnimation && (
								<Box
									sx={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: '100%',
										height: '100%',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										bgcolor: 'rgba(0,0,0,0.3)',
										borderRadius: '8px',
										zIndex: 10,
										pointerEvents: 'none',
									}}
								>
									<Lottie
										animationData={tapAnimation}
										loop
										style={{
											width: 120,
											height: 120,
										}}
									/>
								</Box>
							)}

							{/* Webcam feed */}
							<Webcam
								audio={false}
								ref={webcamRef}
								screenshotFormat="image/jpeg"
								videoConstraints={{
									facingMode: 'environment',
								}}
								onUserMedia={() => {
									setIsWebcamLoaded(true);
									debouncedApplyZoom(zoomLevel);
								}}
								onClick={capture}
								style={{
									width: '100%',
									height: '100%',
									objectFit: 'cover',
									borderRadius: '8px',
									cursor: 'pointer',
								}}
							/>

							{/* Instruction text overlay */}
							<Typography
								variant="caption"
								color="white"
								sx={{
									position: 'absolute',
									top: 0,
									left: 0,
									width: '100%',
									textAlign: 'center',
									backgroundColor: 'rgba(0, 0, 0, 0.6)',
									padding: '6px 8px',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									borderRadius: '8px 8px 0 0',
								}}
							>
								Tap to scan VIN (fit within dotted lines)
							</Typography>

							{/* Dotted guide rectangle */}
							<Box
								sx={{
									position: 'absolute',
									top: '50%',
									left: '50%',
									transform: 'translate(-50%, -50%)',
									width: '90%',
									height: '70%',
									border: '3px dotted white',
									borderRadius: '4px',
									zIndex: 5,
									pointerEvents: 'none',
								}}
							/>
						</Box>

						{/* Zoom controls */}
						<Box
							display="flex"
							flexDirection="row"
							alignItems="center"
							width="100%"
							maxWidth="500px"
							gap={1}
							mt={1}
						>
							{maxZoom > 1 ? (
								<>
									<IconButton
										onClick={() => {
											const newZoom = Math.max(
												zoomLevel - 0.5,
												minZoom,
											);
											setZoomLevel(newZoom);
											debouncedApplyZoom(newZoom);
										}}
										size="small"
									>
										<ZoomOutIcon />
									</IconButton>

									<Slider
										value={zoomLevel}
										min={minZoom}
										max={maxZoom}
										step={0.1}
										onChange={(_, newValue) => {
											setZoomLevel(newValue as number);
											debouncedApplyZoom(newValue as number);
										}}
										onChangeCommitted={() => {
											debouncedApplyZoom.flush();
										}}
										aria-label="Zoom level"
										sx={{ flex: 1 }}
									/>

									<IconButton
										onClick={() => {
											const newZoom = Math.min(
												zoomLevel + 0.5,
												maxZoom,
											);
											setZoomLevel(newZoom);
											debouncedApplyZoom(newZoom);
										}}
										size="small"
									>
										<ZoomInIcon />
									</IconButton>
								</>
							) : (
								<Typography
									variant="caption"
									color="text.secondary"
									width="100%"
									textAlign="center"
								>
									Zoom not supported on this device
								</Typography>
							)}
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default VinScanningComponent;
